import axios from 'axios';
import { ApiResponse, User, WebLoginRequest, AppLoginRequest, AuthResponse } from '../types';
import { API_CONFIG } from '../config/apiConfig';

// Event-related interfaces
export interface EventSession {
  id: string;
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  status: 'active' | 'inactive' | 'ended';
  description?: string;
  qrDisplayUrl?: string;
  qrRefreshInterval: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EventJoinResponse {
  success: boolean;
  message: string;
  gameSessionId: string;
  eventInfo: {
    eventName: string;
    location: string;
    startTime: string;
    endTime: string;
    description?: string;
  };
  attemptInfo?: {
    attemptNumber: number;
    remainingAttempts: number;
    totalAttempts?: number;
  };
}

// Add new interfaces for JWT tokens
export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponseWithTokens extends AuthResponse {
  tokens?: TokenResponse;
  // For direct token fields (backend compatibility)
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  isNewUser?: boolean; // Thêm trường để biết user mới hay cũ
}

// Add new interfaces for Daily Checkin
export interface DailyCheckinStatus {
  currentWeek: number;
  weekStartDate: string;
  weekEndDate: string;
  checkinStreak: number;
  rewards: Array<{
    day: number;
    claimed: boolean;
    available: boolean;
    reward: {
      type: 'energy' | 'special';
      amount: number;
      kilobaSpecies?: string;
    };
  }>;
  canClaimToday: boolean;
  lastCheckinDate?: string;
}

export interface CheckinClaimResponse {
  success: boolean;
  message: string;
  data: {
    energyReward: number;
    kilobaReward?: {
      species: KilobaSpecies;
      userKiloba: UserKiloba;
    };
    newEnergyTotal: number;
    dayInWeek: number;
    weekNumber: number;
    isSpecialReward: boolean;
  };
}

// Pending Actions interface
export interface PendingActions {
  energy: number;
  hasCheckedInToday: boolean;
}

// Kiloba related interfaces
export interface KilobaSpecies {
  id: string;
  name: string;
  code: string;
  description?: string;
  createdAt: string;
}

export interface UserKiloba {
  id: string;
  userId: string;
  kilobaCode: string;
  growthPoints: number;
  isMatured: boolean;
  obtainedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface MiniQuizQuestion {
  id: string;
  imageUrl: string;
  question: string;
  correctAnswer: boolean;
  isActive: boolean;
}

export interface Voucher {
  id: string;
  code: string;
  voucher_type: string;
  value: number;
  status: string;
  description?: string;
  claimed_at?: string;
  expires_at?: string;
}

export interface VoucherWithVisibility {
  id: string;
  code: string | null;
  voucher_type: string;
  value: number;
  status: string;
  created_at: string;
  claimed_at?: string;
  used_at?: string;
  expires_at?: string;
  requiresProfileUpdate: boolean;
  canBeUsed: boolean;
  isExpired: boolean;
}

export interface UserVouchersWithVisibilityResponse {
  vouchers: VoucherWithVisibility[];
  requiresProfileUpdate: boolean;
  profileUpdateMessage?: string;
}

// Notification interfaces
export interface Notification {
  id: string;
  title: string;
  content: string;
  type: 'energy_gained' | 'kiloba_received' | 'kiloba_upgraded' | 'task_completed' | 'voucher_received' | 'daily_checkin' | 'game_reward' | 'system' | 'info';
  status: 'unread' | 'read';
  metadata?: {
    amount?: number;
    sourceType?: string;
    taskId?: string;
    voucherId?: string;
    gameType?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  readAt?: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  unreadCount: number;
}

export interface KilobaUpgradeResponse {
  success: boolean;
  message: string;
  data: {
    kiloba: UserKiloba;
    pointsGained: number;
    matured: boolean;
    newSpecies?: KilobaSpecies;
    newEnergyTotal: number;
    rewardType: 'basic' | 'miniQuiz' | 'voucher10k' | 'voucher20k';
    voucher?: Voucher | null;
    miniQuizQuestion?: MiniQuizQuestion | null;
  };
}

// Kiloba Collection interfaces
export interface KilobaCollectionEntry {
  species: KilobaSpecies;
  quantity: number;
  kilobas: UserKiloba[];
}

export interface KilobaCollectionResponse {
  success: boolean;
  data: KilobaCollectionEntry[];
}

// Kiloba Status interface
export interface KilobaStatus {
  universalKiloba: UserKiloba | null;
  species: KilobaSpecies | null;
  stats: {
    totalKilobas: number;
    maturedKilobas: number;
    growingKilobas: number;
  };
  collection: Array<{
    species: KilobaSpecies;
    quantity: number;
  }>;
}

export interface KilobaStatusResponse {
  success: boolean;
  data: KilobaStatus;
  message?: string;
}

// Create axios instance
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management class
export class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'kiloba_access_token';
  private static readonly REFRESH_TOKEN_KEY = 'kiloba_refresh_token';

  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  static hasValidTokens(): boolean {
    return !!(this.getAccessToken() && this.getRefreshToken());
  }

  static isAuthenticated(): boolean {
    const token = this.getAccessToken();
    // Basic check - in production, you might want to verify token expiry
    return !!token;
  }
}

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = TokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle both 401 (Unauthorized) and 403 (Forbidden) errors
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = TokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/refresh-token`, {
            refreshToken
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          TokenManager.setTokens(accessToken, newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          TokenManager.clearTokens();
          // Don't redirect here - let the component handle authentication state
          return Promise.reject(refreshError);
        }
      } else {
        TokenManager.clearTokens();
        // Don't redirect here - let the component handle authentication state
      }
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authApi = {
  // Web login
  webLogin: async (loginData: WebLoginRequest): Promise<ApiResponse<AuthResponseWithTokens>> => {
    const response = await api.post('/auth/web-login', loginData);
    return response.data;
  },

  // App login
  appLogin: async (loginData: AppLoginRequest): Promise<ApiResponse<AuthResponseWithTokens>> => {
    const response = await api.post('/auth/app-login', loginData);
    return response.data;
  },

  // App login with token (helper method)
  appLoginWithToken: async (token: string): Promise<ApiResponse<AuthResponseWithTokens>> => {
    const response = await api.post('/auth/app-login', { token });
    return response.data;
  },

  // Update user name after phone authentication
  updateName: async (data: { name: string; firebaseToken: string }): Promise<ApiResponse<{ user: User }>> => {
    const response = await api.post('/auth/update-name', data);
    return response.data;
  },

  // Check authentication status
  isAuthenticated: (): boolean => {
    return TokenManager.hasValidTokens();
  },

  // Check current user authentication
  checkAuth: async (): Promise<ApiResponse<{ user: User }>> => {
    const token = TokenManager.getAccessToken();
    if (!token) {
      throw new Error('No access token found');
    }
    
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout: async (): Promise<ApiResponse<null>> => {
    const response = await api.post('/auth/logout');
    TokenManager.clearTokens(); // Clear tokens on logout
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<ApiResponse<TokenResponse>> => {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  // Verify token
  verifyToken: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/auth/verify');
    return response.data;
  }
};

// User API functions
export const userApi = {
  // Get current user profile
  getProfile: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await api.put('/users/profile', userData);
    return response.data;
  },

  // Get user pending actions (energy and checkin status)
  getPendingActions: async (): Promise<ApiResponse<PendingActions>> => {
    const response = await api.get('/users/pending-actions');
    return response.data;
  },

  // Get all users (admin only)
  getAllUsers: async (): Promise<ApiResponse<User[]>> => {
    const response = await api.get('/users');
    return response.data;
  }
};

// Daily Checkin API functions
export const checkinApi = {
  // Get weekly checkin status
  getWeeklyStatus: async (): Promise<ApiResponse<DailyCheckinStatus>> => {
    const response = await api.get('/checkin/status');
    return response.data;
  },

  // Claim daily checkin reward
  claimReward: async (targetDate?: string): Promise<ApiResponse<CheckinClaimResponse>> => {
    const data = targetDate ? { targetDate } : {};
    const response = await api.post('/checkin/claim', data);
    return response.data;
  }
};

// Kiloba API functions
export const kilobaApi = {
  // Get all Kiloba species
  getSpecies: async (): Promise<ApiResponse<KilobaSpecies[]>> => {
    const response = await api.get('/kilobas/species');
    return response.data;
  },

  // Get user's Kilobas
  getUserKilobas: async (userId: string): Promise<ApiResponse<any[]>> => {
    const response = await api.get(`/kilobas/user/${userId}`);
    return response.data;
  },

  // Get user's kiloba collection (mature kilobas grouped by species)
  getUserKilobaCollection: async (userId: string): Promise<ApiResponse<KilobaCollectionEntry[]>> => {
    const response = await api.get(`/kilobas/collection/${userId}`);
    return response.data;
  },

  // Get user's universal Kiloba (growing one)
  getUserUniversalKiloba: async (userId: string): Promise<ApiResponse<UserKiloba>> => {
    const response = await api.get(`/kilobas/universal/${userId}`);
    return response.data;
  },

  // Get user's Kiloba status (comprehensive information)
  getKilobaStatus: async (userId: string): Promise<ApiResponse<KilobaStatus>> => {
    const response = await api.get(`/kilobas/status/${userId}`);
    return response.data;
  },

  // Upgrade Kiloba using energy
  upgradeKiloba: async (): Promise<ApiResponse<KilobaUpgradeResponse['data']>> => {
    const response = await api.post('/kilobas/upgrade');
    return response.data;
  },

  // Update Kiloba growth points
  updateGrowth: async (kilobaId: string, points: number): Promise<ApiResponse<any>> => {
    const response = await api.put(`/kilobas/${kilobaId}/growth`, { points });
    return response.data;
  },

  // Answer mini quiz question for bonus growth point
  answerMiniQuiz: async (questionId: string, answer: boolean): Promise<ApiResponse<{
    correct: boolean;
    bonusGranted: boolean;
    correctAnswer: boolean;
  }>> => {
    const response = await api.post('/kilobas/mini-quiz/answer', { questionId, answer });
    return response.data;
  },

  // Game config API
  getKilobaGamesConfig: async (): Promise<ApiResponse<{
    quiz: {
      dailyPlayLimit: number;
      questionsPerGame: number;
      minimumCorrectAnswers: number;
      energyReward: number;
      description: string;
    };
    racing: {
      dailyPlayLimit: number;
      requiredScore: number;
      energyReward: number;
      description: string;
      remainingAttempts: number;
      canPlay: boolean;
      todayPlays: number;
      todaySuccessful: number;
    };
    clouds: {
      dailyPlayLimit: number;
      requiredScore: number;
      energyReward: number;
      description: string;
      remainingAttempts: number;
      canPlay: boolean;
      todayPlays: number;
      todaySuccessful: number;
    };
  }>> => {
    const response = await api.get('/kiloba-games/config');
    return response.data;
  },

  // Game completion APIs
  completeRacingGame: async (score: number): Promise<ApiResponse<{
    session: {
      id: string;
      gameType: string;
      score: number;
      requiredScore: number;
      status: string;
      energyEarned: number;
      createdAt: string;
    };
    success: boolean;
    energyEarned: number;
    remainingAttempts: number;
    message: string;
  }>> => {
    const response = await api.post('/kiloba-games/racing/complete', { score });
    return response.data;
  },

  complete9CloudsGame: async (score: number): Promise<ApiResponse<{
    success: boolean;
    energyEarned: number;
    remainingAttempts: number;
    message: string;
  }>> => {
    const response = await api.post('/kiloba-games/9clouds/complete', { score });
    return response.data;
  },

  // Get leaderboard for specific game type
  getLeaderboard: async (gameType: string, limit: number = 10): Promise<ApiResponse<{
    userId: string;
    username: string;
    score: number;
    rank: number;
    gameType: string;
    createdAt: string;
  }[]>> => {
    const response = await api.get(`/kiloba-games/leaderboard/${gameType}?limit=${limit}`);
    return response.data;
  },

  // Voucher APIs
  getUserVouchersWithVisibility: async (userId: string, status?: string): Promise<ApiResponse<UserVouchersWithVisibilityResponse>> => {
    let url = `/vouchers/user/${userId}/with-visibility`;
    if (status) {
      url += `?status=${status}`;
    }
    const response = await api.get(url);
    return response.data;
  }
};

// Notification API
export const notificationApi = {
  // Get notifications
  getNotifications: async (params?: {
    status?: 'unread' | 'read';
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<NotificationsResponse>> => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async (): Promise<ApiResponse<{ unreadCount: number }>> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<ApiResponse<Notification>> => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  // Delete notification
  deleteNotification: async (notificationId: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  }
};

// Event API for client
export const eventApi = {
  // Verify QR token and join event (requires authentication)
  verifyAndJoinEvent: async (qrToken: string): Promise<EventJoinResponse> => {
    // Check if user is authenticated
    if (!TokenManager.isAuthenticated()) {
      throw new Error('Authentication required. Please login first.');
    }
    
    const response = await api.post('/events/join', { qrToken });
    return response.data;
  },

  // Get current QR token for an event (public endpoint)
  getCurrentQRToken: async (eventId: string): Promise<{ qrToken: string; expiresIn: number }> => {
    const response = await api.get(`/events/${eventId}/qr-token`);
    return response.data;
  },

  // Submit game score for event session
  submitEventScore: async (gameSessionId: string, score: number): Promise<ApiResponse<{
    success: boolean;
    message: string;
    rewardMessage?: string;
    finalScore?: number;
    voucher?: any;
    voucherResult?: {
      hasVoucher: boolean;
      message: string;
      attemptInfo: {
        attemptNumber: number;
        totalAttempts: number;
        remainingAttempts: number;
        isNewHighestScore: boolean;
        previousHighestScore?: number;
      };
    };
  }>> => {
    console.log('🌐 eventApi.submitEventScore called:', { gameSessionId, score });
    console.log('📡 Sending POST to /events/submit-score');
    const response = await api.post('/events/submit-score', { gameSessionId, score });
    console.log('📡 Event API raw response:', response);
    return response.data;
  }
};

// Monthly Reward interfaces
export interface Top10Winner {
  userId: string;
  name: string;
  phone: string;
  completionTime: string;
  rank: number;
  voucherType: string;
  voucherId: string;
}

export interface Top10Response {
  success: boolean;
  data: {
    hasWinners: boolean;
    winners: Top10Winner[];
    completedMonth: number;
    completedYear: number;
    completedMonthStartDate: string;
    completedMonthEndDate: string;
    currentMonthEndDate?: string; // Chỉ có trong getCurrentMonthTop10
    executionDate?: string;
  };
}

// Monthly Reward API
export const monthlyRewardApi = {
  getCurrentMonthTop10: async (): Promise<Top10Response> => {
    const response = await api.get('/monthly-rewards/top10/current');
    return response.data;
  },

  getTop10ByMonth: async (year: number, month: number): Promise<Top10Response> => {
    const response = await api.get(`/monthly-rewards/top10/${year}/${month}`);
    return response.data;
  }
};

export default api;
